/**
 * cockpit-v2 SectionCard — panel primitive, dual-mode.
 *
 * Usage:
 *   <SectionCard kicker="VITALS" title="Vital sets">
 *     {children}
 *   </SectionCard>
 *
 *   <SectionCard
 *     kicker="UNPOSTED"
 *     title="Awaiting submission"
 *     subtitle="3 runs older than 1h"
 *     status="hot"
 *   >
 *     {children}
 *   </SectionCard>
 *
 * Spec: docs/strategy/EMSPCR_UI_BUILD_SPEC_V1.md §2.2
 *
 * Dual-mode: when cockpitV2 flag is OFF, falls back to the v1
 * `rail-cockpit` ad-hoc panel pattern (matches what the rest of
 * the product looks like today). When ON, renders the v2 chrome
 * with engraved kicker + display-font title + status edge.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useThemeStore } from '@/lib/theme-store';

export type SectionCardStatus = 'idle' | 'hot' | 'satisfied' | 'blocked';

interface SectionCardProps {
  /** Mono-cap engraved label above the title. Optional but
   *  strongly recommended — it carries the cockpit identity. */
  kicker?: string;
  /** Display-font title. Optional; some surfaces (compact strips)
   *  carry kicker + body without a heading. */
  title?: string;
  /** Optional subtitle in the text font. */
  subtitle?: string;
  /** Status edge color on the left rule. Defaults 'idle'.
   *  Use this to thread the same hot/satisfied/blocked vocabulary
   *  as the workflow rail's hot-steel state. */
  status?: SectionCardStatus;
  /** Trailing element on the header row (right-aligned). Useful for
   *  per-card actions like a "+ Add" button or status pill. */
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function SectionCard({
  kicker,
  title,
  subtitle,
  status = 'idle',
  action,
  className,
  children,
}: SectionCardProps) {
  const cockpitV2 = useThemeStore((s) => s.cockpitV2);

  if (!cockpitV2) {
    // Legacy fallback — match today's ad-hoc panel pattern so call
    // sites continue to look identical with v2 off.
    return (
      <section className={cn('rail-cockpit p-5', className)}>
        {(kicker || title || subtitle || action) && (
          <header className={cn('mb-3', (action) && 'flex items-start justify-between gap-3')}>
            <div className="min-w-0">
              {kicker && (
                <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)]">
                  {kicker}
                </p>
              )}
              {title && (
                <h2 className="text-lg font-bold text-[var(--cockpit-text-on-panel)] mt-0.5">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-sm text-[var(--cockpit-text-on-panel-2)] mt-1 leading-relaxed">
                  {subtitle}
                </p>
              )}
            </div>
            {action && <div className="shrink-0">{action}</div>}
          </header>
        )}
        <div className="space-y-3">{children}</div>
      </section>
    );
  }

  // cockpit-v2 rendering
  return (
    <section
      className={cn('section-card-v2', className)}
      data-status={status}
    >
      {(kicker || title || subtitle || action) && (
        <header
          className={cn(
            'section-card-v2-header',
            action && 'flex items-start justify-between gap-3',
          )}
        >
          <div className="min-w-0">
            {kicker && <p className="section-card-v2-kicker">{kicker}</p>}
            {title && <h2 className="section-card-v2-title">{title}</h2>}
            {subtitle && <p className="section-card-v2-subtitle">{subtitle}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </header>
      )}
      <div className="section-card-v2-body">{children}</div>
    </section>
  );
}
